import { generateFilterFields } from 'app/forms/config/helpers';
import { Select, Switch } from 'core/form';
import React from 'react';
import { viewTypeOptions } from '../../$dataviews';

const BASE_CHART_FIELDNAME = 'config.chart';
const BASE_DATA_FIELDNAME = 'config.data';
const BASE_TABLE_FIELDNAME = 'config.table';

interface Option {
  label: string;
  value: string | number;
}

type FormComponent = typeof Select | typeof Switch;

interface FieldConfig<T extends FormComponent = FormComponent> {
  label: string;
  formComponent?: T;
  options?: Option[];
  defaultValue?: unknown;
  rules?: string;
  messages?: Record<string, string>;
  formComponentProps?: React.ComponentProps<T>;
}

type FieldConfigDict = Record<string, FieldConfig>;

export const xAxisFields: FieldConfigDict = {
  [`${BASE_CHART_FIELDNAME}.xAxis.timezone`]: {
    label: 'Time Zone',
    formComponent: Select
  },
  [`${BASE_CHART_FIELDNAME}.xAxis.tickInterval`]: {
    formComponent: Select,
    options: [
      { label: 'Auto', value: 'auto' },
      { label: '1 minute', value: '1m' },
      { label: '5 minutes', value: '5m' },
      { label: '10 minutes', value: '10m' },
      { label: '1 hour', value: '1h' },
      { label: '1 day', value: '1d' }
    ],
    label: 'Tick Interval'
  }
} as const;

export const yAxisFields: FieldConfigDict = {
  [`${BASE_CHART_FIELDNAME}.yAxis.metric`]: {
    label: 'Charted metric',
    formComponent: Select,
    options: []
  },
  [`${BASE_CHART_FIELDNAME}.yAxis.multipleAxis`]: {
    label: 'Multiple Axis',
    formComponent: Switch,
    defaultValue: false
  },
  [`${BASE_CHART_FIELDNAME}.yAxis.aggregation`]: {
    label: 'Aggregation',
    formComponent: Select,
    options: []
  },
  [`${BASE_CHART_FIELDNAME}.yAxis.type`]: {
    label: 'Scale',
    formComponent: Select,
    options: [
      { label: 'Linear', value: 'linear' },
      { label: 'Logarithmic', value: 'logarithmic' },
      { label: 'Datetime', value: 'datetime' },
      { label: 'Categorical', value: 'category' }
    ],
    defaultValue: 'linear'
  },
  [`${BASE_CHART_FIELDNAME}.yAxis.tickFormat`]: {
    label: 'Tick Format',
    formComponent: Select,
    options: [
      { label: 'Default', value: 'default' },
      { label: 'Percent', value: 'percent' },
      { label: 'Currency', value: 'currency' }
    ]
  }
} as const;

export const legendFields: FieldConfigDict = {
  [`${BASE_CHART_FIELDNAME}.legend.enable`]: {
    label: 'Show Legend',
    defaultValue: true
  },
  [`${BASE_CHART_FIELDNAME}.legend.position`]: {
    label: 'Position',
    formComponent: Select,
    defaultValue: 'bottom',
    options: [
      { label: 'Bottom', value: 'bottom' },
      { label: 'Right', value: 'right' },
      { label: 'Left', value: 'left' }
    ]
  },
  [`${BASE_CHART_FIELDNAME}.legend.mode`]: {
    label: 'Mode',
    formComponent: Select,
    defaultValue: 'table',
    options: [
      { label: 'Table', value: 'table' },
      { label: 'Condensed', value: 'condensed' }
    ]
  },
  [`${BASE_CHART_FIELDNAME}.legend.showRollups`]: {
    label: 'Show Rollups',
    defaultValue: true
  },
  [`${BASE_CHART_FIELDNAME}.legend.showEventsView`]: {
    label: 'Show Events View',
    defaultValue: true
  }
} as const;

export const thresholdFields: FieldConfigDict = {
  [`${BASE_CHART_FIELDNAME}.thresholds.enabled`]: {
    label: 'Enable thresholds',
    defaultValue: false
  },
  [`${BASE_CHART_FIELDNAME}.thresholds.mode`]: {
    label: 'Mode',
    formComponent: Select,
    options: [
      { value: 'absolute', label: 'Absolute' },
      { value: 'percentage', label: 'Percentage' }
    ],
    defaultValue: 'percentage'
  },
  [`${BASE_CHART_FIELDNAME}.thresholds.display`]: {
    label: 'Show thresholds',
    formComponent: Select,
    options: [
      { value: 'line', label: 'As lines' },
      { value: 'dashed-line', label: 'As lines (dashed)' },
      { value: 'area', label: 'As filled regions' },
      { value: 'line+area', label: 'As filled regions and lines' },
      { value: 'dashed-line+area', label: 'As filled regions and lines (dashed)' }
    ],
    defaultValue: 'line+area'
  },
  [`${BASE_CHART_FIELDNAME}.thresholds.colorSeries`]: {
    label: 'Color entire series',
    defaultValue: false
  },
  [`${BASE_CHART_FIELDNAME}.thresholds.steps`]: {
    label: 'Steps',
    defaultValue: [
      { value: null, color: '#73BF69' },
      { value: 50, color: '#FF9830' },
      { value: 80, color: '#F2495C' }
    ]
  }
} as const;

export const overlayFields: FieldConfigDict = {
  [`${BASE_CHART_FIELDNAME}.showTotalOverlay`]: {
    label: 'Show total overlay',
    defaultValue: false
  },
  [`${BASE_CHART_FIELDNAME}.showHistoricalOverlay`]: {
    label: 'Show historical overlay',
    defaultValue: false
  }
} as const;

export const tableFields: FieldConfigDict = {
  [`${BASE_TABLE_FIELDNAME}.enable`]: {
    label: 'Show table',
    defaultValue: true
  },
  [`${BASE_TABLE_FIELDNAME}.showTotalRow`]: {
    label: 'Show total row',
    defaultValue: true
  },
  [`${BASE_TABLE_FIELDNAME}.columns.visible`]: {
    label: 'Visible Columns',
    formComponent: Select
  },
  [`${BASE_TABLE_FIELDNAME}.columns.hidden`]: {
    label: 'Hidden Columns',
    formComponent: Select
  }
} as const;

export const tooltipFields: FieldConfigDict = {
  [`${BASE_CHART_FIELDNAME}.tooltip.enable`]: {
    label: 'Show table tooltip',
    defaultValue: true
  },
  [`${BASE_CHART_FIELDNAME}.tooltip.format`]: {
    label: 'Table tooltip format'
  }
} as const;

export const dataFields: FieldConfigDict = {
  [`${BASE_DATA_FIELDNAME}.measurement`]: {
    label: 'Measurement',
    rules: 'required'
  },
  [`${BASE_DATA_FIELDNAME}.metrics`]: {
    label: 'Metrics',
    rules: 'array|min:1|max:10',
    defaultValue: [],
    messages: {
      min: 'You must have at least 1 unit selected.',
      max: 'You cannot have more than 10 units selected at one time.'
    }
  },
  [`${BASE_DATA_FIELDNAME}.dimensions`]: {
    label: 'Group by Dimensions',
    rules: 'array|max:20',
    defaultValue: [],
    messages: {
      max: 'You cannot have more than 20 dimensions selected at one time.'
    }
  },
  [`${BASE_DATA_FIELDNAME}.reAgg.fn`]: {
    label: 'Merge Series Function',
    defaultValue: 'avg',
    options: [
      { label: 'Average', value: 'avg' },
      { label: 'Sum', value: 'sum' },
      { label: 'Min', value: 'min' },
      { label: 'Max', value: 'max' }
    ]
  },
  [`${BASE_DATA_FIELDNAME}.rollups`]: {
    label: 'Rollups',
    defaultValue: []
  },
  [`${BASE_DATA_FIELDNAME}.sort`]: {
    label: 'Sort',
    defaultValue: []
  },
  [`${BASE_DATA_FIELDNAME}.time.lookback`]: {
    label: 'Lookback',
    defaultValue: 3 * 3600
  },
  [`${BASE_DATA_FIELDNAME}.time.start`]: {
    label: 'Start Time',
    defaultValue: 0
  },
  [`${BASE_DATA_FIELDNAME}.time.end`]: {
    label: 'End Time',
    defaultValue: 0
  },
  [`${BASE_DATA_FIELDNAME}.window.fn`]: {
    label: 'Aggregation Function',
    defaultValue: {}
  },
  [`${BASE_DATA_FIELDNAME}.window.size`]: {
    label: 'Bucket Size',
    defaultValue: 0
  },
  [`${BASE_DATA_FIELDNAME}.viz.limit`]: {
    label: 'Show Series',
    formComponent: Select,
    defaultValue: 10,
    options: [
      { label: '5', value: 5 },
      { label: '10', value: 10 },
      { label: '15', value: 15 },
      { label: '20', value: 20 },
      { label: '25', value: 25 },
      { label: '30', value: 30 },
      { label: '40', value: 40 }
    ]
  },
  [`${BASE_DATA_FIELDNAME}.limit`]: {
    label: 'Table Depth',
    defaultValue: 100
  },
  [`${BASE_DATA_FIELDNAME}.updateFrequency`]: {
    label: 'Auto Refresh Frequency',
    defaultValue: 0
  },
  ...generateFilterFields(`${BASE_DATA_FIELDNAME}.filters.dimensions`),
  ...generateFilterFields(`${BASE_DATA_FIELDNAME}.filters.metrics`, [], { isMetricFilter: true })
} as const;

export default {
  ...dataFields,

  [`${BASE_CHART_FIELDNAME}.enable`]: {
    label: 'Enable chart',
    defaultValue: true
  },
  [`${BASE_CHART_FIELDNAME}.type`]: {
    label: 'Chart type',
    options: viewTypeOptions,
    defaultValue: 'line'
  },

  [`${BASE_CHART_FIELDNAME}.colors`]: {
    label: 'Colors',
    defaultValue: 'default'
  },

  // chart options
  ...xAxisFields,
  ...yAxisFields,
  ...legendFields,
  ...overlayFields,
  ...tooltipFields,
  ...thresholdFields
} as const;
